import {
  Box,
  MenuItem,
  Typography,
  Button,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomSelect from '../../../../components/CustomSelect';
import { ErrorMessageSnackbar } from '../../../../components/ErrorMessageSnackbar';
import MultiSelect from '../../../../components/MultiSelect';
import { useGetRunIds } from '../../../../hooks/query/useMatchFlow';
import { useMatchEngineStatistics } from '../../../../hooks/query/useStatistics';
import useDebounce from '../../../../hooks/useDebounce';
import { ChevronDown } from '../../../../icons/chevron-down';
import { Refresh } from '../../../../icons/refresh';
import STRING_KEYS from '../../../../language/keys';
import { EngineStatusCards } from './EngineStatusCards';
import { style } from './style';

const STATS_FOR_OPTIONS = {
  ALL_RUNS: 'allRuns',
  PER_RUN: 'perRun',
};

type MatchEngineStatsProps = {
  viewMode: ViewMode,
  positionId: string,
  accountId: string,
}

export const MatchEngineStats = ({
  viewMode,
  accountId,
  positionId,
}: MatchEngineStatsProps):JSX.Element => {
  const { t: translate } = useTranslation();
  const [selectedStatsForOption, setSelectedStatsForOption] = useState<string>(STATS_FOR_OPTIONS.ALL_RUNS);
  const [runIdQuery, setRunIdQuery] = useState<string>('');
  const [selectedRunIds, setSelectedRunIds] = useState<string[]>([]);

  const {
    statistics,
    refetch,
    isFetching,
    isError,
  } = useMatchEngineStatistics(positionId, accountId, selectedRunIds);

  const { debouncedValue: debouncedRunIdQuery } = useDebounce(runIdQuery);
  const { filterOptions, isLoading } = useGetRunIds(
    accountId,
    positionId,
    debouncedRunIdQuery,
  );

  useEffect(() => {
    refetch();
  }, [refetch, selectedRunIds]);

  const menu = [
    {
      id: STATS_FOR_OPTIONS.ALL_RUNS,
      title: translate(STRING_KEYS.ALL_RUNS),
    },
    {
      id: STATS_FOR_OPTIONS.PER_RUN,
      title: translate(STRING_KEYS.PER_RUN),
    },
  ];

  const handleRunIdQueryChange = (value: string) => {
    setRunIdQuery(value);
  };

  const handleChangeRunId = (value: string[]) => {
    setSelectedRunIds(value);
  };

  const handleSelectedStatsForOptionChange = (event: SelectChangeEvent<unknown>) => {
    setSelectedStatsForOption(event.target.value as string);
  };

  const renderMenu = (): React.ReactNode => {
    return menu.map((item) => {
      return (
        <MenuItem
          key={ item.id }
          value={ item.id }
        >
          <Typography variant="body1" sx={ style.menuItemLabel.sx } color="primary">
            {item.title}
          </Typography>

        </MenuItem>
      );
    });
  };

  return (
    <Box sx={ style.BoxContainer.sx }>
      <ErrorMessageSnackbar
        showError={ isError }
        errorMessage={ translate(STRING_KEYS.MATCH_ENGINE_STATISTICS_ERROR) }
      />
      <Box sx={ style.BoxWrapper.sx }>
        <Box sx={ style.row.sx }>
          <Typography variant="subtitle1" sx={ style.Typography.sx }>
            {translate(STRING_KEYS.START_FOR)}
          </Typography>
          <CustomSelect
            viewMode={ viewMode }
            variant="standard"
            labelId="Runs"
            id="Runs"
            sx={ style.Select.sx }
            value={ selectedStatsForOption }
            name="runs"
            onChange={ handleSelectedStatsForOptionChange }
            IconComponent={ () => (
              <ChevronDown color="primary" sx={ style.iconComponent.sx } />
            ) }
          >
            {renderMenu()}
          </CustomSelect>
          {selectedStatsForOption === STATS_FOR_OPTIONS.PER_RUN && (
            <MultiSelect
              label={ translate(STRING_KEYS.RUN_ID) }
              onChange={ handleChangeRunId }
              options={ filterOptions }
              value={ selectedRunIds }
              autoComplete
              autoCompleteOnChange={ handleRunIdQueryChange }
              autoCompleteValue={ runIdQuery }
              isLoading={ isLoading }
            />
          )}
        </Box>
        <Box sx={ style.IconWrapper.sx }>
          {isFetching ? (
            <CircularProgress sx={ style.loader.sx } size={ style.loader.loaderSize } />
          )  :
            (
              <Button
                variant="text"
                onClick={ refetch }
                startIcon={ (
                  <Refresh />
                ) }
              />
            )}
        </Box>
      </Box>
      <EngineStatusCards
        resultsAmount={ statistics.total.toString() }
        declineAmount={ statistics.declineRate.toFixed(2) }
        relevanceAmount={ statistics.relevanceRate.toFixed(2) }
      />
    </Box>
  );
};
